import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Instagram, Facebook, Youtube, Mail, Phone, MapPin, Shield, ArrowUpRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setNewsletterStatus({
        type: response.data.status === 'info' ? 'info' : 'success',
        message: response.data.message
      });
      if (response.data.status === 'success') {
        setEmail('');
      }
    } catch (error) {
      setNewsletterStatus({
        type: 'error',
        message: 'Failed to subscribe. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setNewsletterStatus(null), 5000);
    }
  };

  const footerLinks = {
    services: [
      { name: 'Specialised Investment Services', path: '/wealth-builder' },
      { name: 'Goal Planning', path: '/goal-planner' },
      { name: 'Retirement Planning', path: '/retirement' },
      { name: 'Insurance Plans', path: '/insurance' },
      { name: 'Tax Optimization', path: '/tax-optimization' },
      { name: 'NRI Connect', path: '/nri' },
    ],
    resources: [
      { name: 'Calculators', path: '/calculators' },
    ],
    company: [
      { name: 'About Us', path: '/team' },
      { name: 'Contact', path: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy-policy' },
      { name: 'Terms of Service', path: '/terms-of-service' },
      { name: 'Disclaimer', path: '/disclaimer' },
    ],
  };

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Youtube, href: '#', label: 'YouTube' },
  ];

  return (
    <footer className="bg-black text-white">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl lg:text-3xl font-bold mb-2">
                You're building your future,
              </h3>
              <p className="text-white/60">
                let us help you secure it. Subscribe to our insights.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
                className="px-6 py-3 bg-white/5 border border-white/10 rounded-full text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 w-full sm:w-80 disabled:opacity-50"
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-white text-black font-medium rounded-full hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  'Subscribe'
                )}
              </button>
            </form>
          </div>
          {newsletterStatus && (
            <div className={`mt-4 text-center lg:text-right text-sm ${
              newsletterStatus.type === 'success' ? 'text-emerald-400' : 
              newsletterStatus.type === 'info' ? 'text-blue-400' : 'text-red-400'
            }`}>
              {newsletterStatus.message}
            </div>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dezerv-frontier/artifacts/gf02e7f8_Untitled%20%28500%20x%20200%20px%29%20%281%29.png" 
                alt="Frontier Investments" 
                className="h-24 w-auto object-contain"
              />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-sm">
              Your trusted wealth mentor. We help you build wealth strategically,
              simplifying your finances and easing your worries.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="p-2.5 bg-white/5 rounded-full hover:bg-white/10 transition-colors duration-300"
                >
                  <social.icon size={18} className="text-white/70" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-white/60 text-sm hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-white/60 text-sm hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-white/60 text-sm hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-white/60 text-sm hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/5 rounded-full">
                <Mail size={20} className="text-white/70" />
              </div>
              <div>
                <p className="text-white/40 text-xs">Email</p>
                <p className="text-white text-sm">contact@frontierinvestments.co</p>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance & Copyright */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full">
                <Shield size={16} className="text-emerald-500" />
                <span className="text-white/60 text-xs">AMFI Certified MFD</span>
              </div>
              <span className="text-white/40 text-xs">ARN-92591</span>
            </div>
            <p className="text-white/40 text-xs text-center">
              © {currentYear} Frontier Investments. All Rights Reserved.
            </p>
            <p className="text-white/40 text-xs text-center">
              Mutual Fund investments are subject to market risks. Please read all scheme related documents carefully.
            </p>
          </div>
          <div className="mt-6 text-center">
            <p className="text-white/50 text-sm">
              Website Powered by <a href="https://techbook.co.in/" target="_blank" rel="noopener noreferrer" className="text-white/70 font-medium hover:text-white transition-colors">Techbook Technologies</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
